import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Phone, Calendar, ChevronDown, Copy, Check, Play,
  AlertTriangle, Users, CreditCard, CircleCheck, Link2
} from 'lucide-react';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

/* ─── Right Sidebar Widget ─── */
const Widget = ({ title, action, actionLabel, children }) => (
  <div className="py-4 border-b border-gray-100 dark:border-white/10 last:border-0" data-testid={`widget-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {action && (
        <button onClick={action} className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
          {actionLabel || 'Manage'}
        </button>
      )}
    </div>
    {children}
  </div>
);

/* ─── Call Log Entry ─── */
const CallEntry = ({ log }) => {
  const navigate = useNavigate();
  const time = log.created_at ? new Date(log.created_at) : null;
  const duration = log.transcript_entries?.length
    ? `${Math.max(1, Math.round(log.transcript_entries.length * 0.7))}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`
    : '0:00';

  const sentimentColor = {
    positive: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
    neutral: 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300',
    negative: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
  };

  const sentimentLabel = {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
  };

  const openContact = () => {
    if (log.caller_number) navigate(`/crm?phone=${encodeURIComponent(log.caller_number)}`);
    else navigate('/crm');
  };

  return (
    <div
      onClick={openContact}
      role="button"
      title="Open this caller in CRM"
      className="flex items-start gap-3 py-4 border-b border-gray-50 dark:border-white/5 last:border-0 group hover:bg-gray-50/50 dark:hover:bg-white/5 -mx-2 px-2 rounded-lg transition-colors cursor-pointer"
      data-testid={`call-entry-${log.id || log.call_sid}`}
    >
      <button onClick={(e) => e.stopPropagation()} className="mt-1 w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors" data-testid="play-call-btn">
        <Play className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 ml-0.5" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 dark:text-gray-100 leading-relaxed">
          {log.summary || `Call from ${log.caller_number || 'Unknown number'}`}
        </p>
        <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
          <span>{duration}</span>
          <span className="text-gray-300">&#8226;</span>
          <span>{log.caller_number || 'Unknown'}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 mt-1">
        {log.sentiment && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${sentimentColor[log.sentiment] || sentimentColor.neutral}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${log.sentiment === 'positive' ? 'bg-green-500' : log.sentiment === 'negative' ? 'bg-red-500' : 'bg-gray-400'}`} />
            {sentimentLabel[log.sentiment] || 'Neutral'}
          </span>
        )}
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {time ? time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }) : ''}
        </span>
      </div>
    </div>
  );
};

/* ─── Setup Progress Ring ─── */
const SetupRing = ({ completed, total }) => {
  const pct = total > 0 ? (completed / total) * 100 : 0;
  const r = 12;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" className="flex-shrink-0">
      <circle cx="16" cy="16" r={r} fill="none" stroke="#e5e7eb" strokeWidth="3" />
      <circle cx="16" cy="16" r={r} fill="none" stroke="#3b82f6" strokeWidth="3"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 16 16)" className="transition-all duration-500" />
    </svg>
  );
};

/* ═══════════════════════════════════════════
   ███ MAIN DASHBOARD COMPONENT ███
   ═══════════════════════════════════════════ */
const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [callLogs, setCallLogs] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [setupProgress, setSetupProgress] = useState({ tasks: [], completed: 0, total: 0 });
  const [receptionOn, setReceptionOn] = useState(true);
  const [activeTab, setActiveTab] = useState('calls');
  const [copied, setCopied] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [logsRes, subRes, phoneRes, progressRes, contactsRes] = await Promise.allSettled([
        axios.get(`${API}/calls/logs`, { headers }),
        axios.get(`${API}/payments/subscription`, { headers }),
        axios.get(`${API}/phone/my-numbers`, { headers }),
        axios.get(`${API}/business/setup-progress`, { headers }),
        axios.get(`${API}/crm/contacts`, { headers }),
      ]);
      if (logsRes.status === 'fulfilled') setCallLogs(logsRes.value.data);
      if (subRes.status === 'fulfilled') setSubscription(subRes.value.data);
      if (phoneRes.status === 'fulfilled') setPhoneNumbers(phoneRes.value.data);
      if (progressRes.status === 'fulfilled') setSetupProgress(progressRes.value.data);
      if (contactsRes.status === 'fulfilled') setContacts(contactsRes.value.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const primaryPhone = phoneNumbers.length > 0 ? phoneNumbers[0] : null;
  const formatPhone = (num) => {
    if (!num) return 'No number assigned';
    const clean = num.replace(/\D/g, '');
    if (clean.length === 11 && clean.startsWith('1')) {
      return `+1 ${clean.slice(1, 4)} - ${clean.slice(4, 7)} - ${clean.slice(7)}`;
    }
    return num;
  };

  const copyPhone = () => {
    if (primaryPhone?.phone_number) {
      navigator.clipboard.writeText(primaryPhone.phone_number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const hasSub = subscription && subscription.status !== 'none';
  const callLimit = hasSub ? (subscription.plan_type?.includes('scale') ? 1500 : subscription.plan_type?.includes('growth') ? 750 : 450) : 0;

  // Group call logs by date
  const groupedCalls = callLogs.reduce((groups, log) => {
    const date = log.created_at ? new Date(log.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Unknown';
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const label = date === today ? 'Today' : date === yesterday ? 'Yesterday' : date;
    if (!groups[label]) groups[label] = [];
    groups[label].push(log);
    return groups;
  }, {});

  // Recent leads from contacts
  const recentLeads = contacts.filter(c => c.status === 'new' || c.lead_captured).slice(0, 10);

  const tabs = [
    { key: 'calls', label: 'Calls' },
    { key: 'leads', label: 'Leads' },
    { key: 'integrations', label: 'Integrations' },
    { key: 'workflows', label: 'Workflows' },
  ];

  return (
    <div className="flex bg-white dark:bg-[#0b1a30]" data-testid="dashboard">
          <main className="flex-1 min-w-0" data-testid="main-content">
            <div className="max-w-4xl mx-auto px-4 lg:px-8 py-8">
              {/* Title + Phone Number Row */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-6">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white" data-testid="reception-title">Your Reception</h1>
                  <p className="text-gray-500 text-sm mt-1">Monitor and manage your AI receptionist</p>

                  {/* Setup Progress */}
                  {setupProgress.total > 0 && setupProgress.completed < setupProgress.total && (
                    <div className="flex items-center gap-3 mt-4 px-4 py-2.5 border border-gray-200 dark:border-white/10 rounded-full w-fit" data-testid="setup-progress">
                      <SetupRing completed={setupProgress.completed} total={setupProgress.total} />
                      <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">Get set up</span>
                      <span className="text-xs text-gray-400">{setupProgress.completed}/{setupProgress.total} tasks</span>
                      <button onClick={() => navigate('/business-setup')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors" data-testid="continue-setup-btn">
                        Continue Setup &rarr;
                      </button>
                    </div>
                  )}
                  {setupProgress.total > 0 && setupProgress.completed >= setupProgress.total && (
                    <div className="flex items-center gap-2 mt-4 px-4 py-2.5 border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 rounded-full w-fit" data-testid="setup-complete">
                      <CircleCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-green-700 dark:text-green-300 font-medium">Setup complete</span>
                    </div>
                  )}
                </div>

                {/* Phone Number Card */}
                <div className="bg-white dark:bg-[#0b1a30] border border-gray-200 dark:border-white/10 rounded-xl px-5 py-4 min-w-[260px]" data-testid="phone-number-card">
                  <p className="text-xs text-gray-400 font-medium mb-1">Your AI Receptionist's Number</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white tracking-wide" data-testid="phone-number-display">
                      {primaryPhone ? formatPhone(primaryPhone.phone_number) : 'No number yet'}
                    </span>
                    {primaryPhone && (
                      <button onClick={copyPhone} className="text-gray-400 hover:text-blue-600 transition-colors" data-testid="copy-phone-btn">
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                  <button onClick={() => navigate('/phone-setup')} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mt-1 inline-flex items-center gap-1 transition-colors" data-testid="manage-number-btn">
                    Manage Your Number <ChevronDown className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 dark:border-white/10 mb-6" data-testid="dashboard-tabs">
                <div className="flex gap-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        if (tab.key === 'integrations') navigate('/integrations');
                        else if (tab.key === 'workflows') navigate('/integrations?tab=workflows');
                        else setActiveTab(tab.key);
                      }}
                      data-testid={`tab-${tab.key}`}
                      className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.key
                          ? 'text-blue-600 dark:text-blue-400 border-blue-600'
                          : 'text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-white/30'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'calls' && (
                <div data-testid="calls-tab-content">
                  {loading ? (
                    <div className="py-12 text-center text-gray-400 text-sm">Loading calls...</div>
                  ) : callLogs.length === 0 ? (
                    <div className="py-16 text-center" data-testid="no-calls-msg">
                      <Phone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-1">No calls yet</h3>
                      <p className="text-sm text-gray-400 mb-4">Once your AI receptionist handles calls, they'll appear here.</p>
                      <button onClick={() => navigate('/phone-setup')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300" data-testid="setup-phone-cta">
                        Set Up Your Phone Number &rarr;
                      </button>
                    </div>
                  ) : (
                    <div>
                      {Object.entries(groupedCalls).map(([dateLabel, logs]) => (
                        <div key={dateLabel} className="mb-6">
                          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{dateLabel}</h3>
                          {logs.map((log) => (
                            <CallEntry key={log.id || log.call_sid} log={log} />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'leads' && (
                <div data-testid="leads-tab-content">
                  {recentLeads.length === 0 ? (
                    <div className="py-16 text-center">
                      <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-1">No leads captured yet</h3>
                      <p className="text-sm text-gray-400 mb-4">Leads from calls will appear here automatically.</p>
                      <button onClick={() => navigate('/crm')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                        Go to CRM &rarr;
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {recentLeads.map((lead, i) => (
                        <div
                          key={lead.id || i}
                          onClick={() => navigate(lead.id ? `/crm?contact=${lead.id}` : '/crm')}
                          role="button"
                          title="Open in CRM"
                          data-testid={`lead-entry-${lead.id || i}`}
                          className="flex items-center gap-3 py-3 border-b border-gray-50 dark:border-white/5 last:border-0 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-white/5 -mx-2 px-2 rounded-lg transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">
                            {(lead.name || lead.phone || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{lead.name || lead.phone || 'Unknown'}</p>
                            <p className="text-xs text-gray-400 truncate">{lead.email || lead.phone || ''}</p>
                          </div>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                            lead.status === 'new' ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400' :
                            lead.status === 'qualified' ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400' :
                            lead.status === 'customer' ? 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400' :
                            'bg-gray-50 dark:bg-white/5 text-gray-500'
                          }`}>{lead.status || 'new'}</span>
                        </div>
                      ))}
                      <button onClick={() => navigate('/crm')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-2 block">
                        View all contacts &rarr;
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'integrations' && (
                <div className="py-16 text-center" data-testid="integrations-tab-content">
                  <Link2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-1">Integrations</h3>
                  <p className="text-sm text-gray-400 mb-4">Connect your tools and services to Miadesk.</p>
                  <button onClick={() => navigate('/integrations')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                    Manage Integrations &rarr;
                  </button>
                </div>
              )}

              {activeTab === 'workflows' && (
                <div className="py-16 text-center" data-testid="workflows-tab-content">
                  <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-1">Workflows</h3>
                  <p className="text-sm text-gray-400 mb-4">Automate call handling, lead routing, and follow-ups.</p>
                  <button onClick={() => navigate('/integrations?tab=workflows')} className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                    Configure Workflows &rarr;
                  </button>
                </div>
              )}
            </div>
          </main>

          {/* ═══ RIGHT SIDEBAR ═══ */}
          <aside className="hidden xl:block w-80 border-l border-gray-200 dark:border-white/10 bg-[#fafbfc] dark:bg-[#0d1f35] flex-shrink-0" data-testid="right-sidebar">
            <div className="px-5 py-6">
              {/* Reception Status */}
              <Widget title="Reception Status">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={receptionOn}
                      onCheckedChange={setReceptionOn}
                      data-testid="reception-toggle"
                    />
                    <span className={`text-sm font-medium ${receptionOn ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                      {receptionOn ? 'On, taking calls' : 'Off, paused'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500" data-testid="call-usage">
                    {callLogs.length}/{callLimit || '∞'} calls used
                  </span>
                  {hasSub ? (
                    <button onClick={() => { navigate('/'); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 300); }} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium" data-testid="upgrade-plan-btn">
                      Upgrade Plan
                    </button>
                  ) : (
                    <button onClick={() => { navigate('/'); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 300); }} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium" data-testid="subscribe-btn">
                      Subscribe
                    </button>
                  )}
                </div>
              </Widget>

              {/* Booking Link */}
              <Widget title="Booking Link" action={() => navigate('/appointments')} actionLabel="Manage">
                <button onClick={() => navigate('/appointments')} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#0b1a30] border border-gray-200 dark:border-white/10 rounded-lg w-full hover:border-blue-300 dark:hover:border-blue-500/50 transition-colors">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-200 flex-1 text-left">Add Booking Link</span>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </Widget>

              {/* Lead Intake */}
              <Widget title="Lead Intake" action={() => navigate('/crm')} actionLabel="Manage">
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="w-2 h-2 rounded-full bg-gray-300" />
                  <span className="text-xs text-gray-400">
                    {contacts.length > 0 ? `${contacts.length} contacts captured` : 'No active form for reception'}
                  </span>
                </div>
              </Widget>

              {/* Escalation */}
              <Widget title="Escalation" action={() => navigate('/tickets')} actionLabel="Manage">
                <p className="text-xs text-gray-400">Configure when and how calls get escalated to humans.</p>
              </Widget>

              {/* Subscription */}
              {hasSub && (
                <Widget title="Subscription">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100 capitalize">
                      {subscription.plan_type?.replace('_annual', '')} Plan
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 capitalize">Status: {subscription.status}</p>
                </Widget>
              )}
            </div>
          </aside>
    </div>
  );
};

export default Dashboard;
